import type {
  DiagnosisUnlockModalPayload,
  DiagnosisUnlockGoalDraft,
  DiagnosisUnlockRiskItem,
} from "./Steps/DiagnosisUnlockFlow/types";
import type {
  PlanCheckupCompletedCardData,
  PlanCheckupDomainId,
} from "@/src/screens/plan/constants/planCheckupTypes";
import type { FitnessDimensionType } from "@/src/components/fitness/types";
import { PLAN_CHECKUP_DIMENSIONS } from "@/src/screens/plan/constants/planCheckupDimensions";
import {
  GoalCategoryEnum,
  GoalStatusEnum,
} from "@/src/features/api/TrackerApi";

export type PresentRiskAndGoalsArgsLike = {
  summary?: unknown;
  riskSummary?: unknown;
  title?: unknown;
  riskItems?: unknown;
  goalDraft?: unknown;
  /** API 下发 GoalCategoryEnum，如 WEALTH_BUILDING */
  category?: unknown;
  domainId?: unknown;
  domainLabel?: unknown;
  checkupType?: unknown;
};

export type ParsedPresentRiskAndGoals = {
  payload: DiagnosisUnlockModalPayload;
  card: PlanCheckupCompletedCardData;
};

const RISK_LEVELS: DiagnosisUnlockRiskItem["riskLevel"][] = [
  "high",
  "medium",
  "low",
];
const DOMAIN_IDS: PlanCheckupDomainId[] = [
  "financial_security",
  "cash_optimization",
  "wealth_building",
  "life_decisions",
];

const CATEGORY_TO_DOMAIN: Record<GoalCategoryEnum, PlanCheckupDomainId> = {
  [GoalCategoryEnum.FINANCIAL_SECURITY]: "financial_security",
  [GoalCategoryEnum.CASH_OPTIMIZATION]: "cash_optimization",
  [GoalCategoryEnum.WEALTH_BUILDING]: "wealth_building",
  [GoalCategoryEnum.LIFE_DECISIONS]: "life_decisions",
};

const GOAL_CATEGORY_VALUES = new Set<string>(Object.values(GoalCategoryEnum));

const isDomainId = (value: unknown): value is PlanCheckupDomainId =>
  typeof value === "string" &&
  DOMAIN_IDS.includes(value as PlanCheckupDomainId);

const parseCategoryToDomainId = (
  category: unknown,
): PlanCheckupDomainId | null => {
  if (typeof category !== "string" || !GOAL_CATEGORY_VALUES.has(category)) {
    return null;
  }

  return CATEGORY_TO_DOMAIN[category as GoalCategoryEnum];
};

const resolveCardDomainId = (
  raw: PresentRiskAndGoalsArgsLike,
): PlanCheckupDomainId | null =>
  (isDomainId(raw.domainId) ? raw.domainId : null) ??
  parseCategoryToDomainId(raw.category);

const getDefaultDimensionForDomain = (
  domainId: PlanCheckupDomainId,
): number => {
  const meta = PLAN_CHECKUP_DIMENSIONS.find(
    (item) => item.domainId === domainId,
  );
  return meta?.dimension ?? 0;
};

const getDefaultDimensionTypeForDomain = (
  domainId: PlanCheckupDomainId,
): FitnessDimensionType => {
  const meta = PLAN_CHECKUP_DIMENSIONS.find(
    (item) => item.domainId === domainId,
  );
  return meta?.dimensionType ?? "control";
};

const isRiskLevel = (
  value: unknown,
): value is DiagnosisUnlockRiskItem["riskLevel"] =>
  typeof value === "string" &&
  RISK_LEVELS.includes(value as DiagnosisUnlockRiskItem["riskLevel"]);

const parseRiskItem = (item: unknown): DiagnosisUnlockRiskItem | null => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const { title, description, riskLevel } = item as Record<string, unknown>;
  if (typeof title !== "string" || !title.trim()) {
    return null;
  }
  if (typeof description !== "string" || !description.trim()) {
    return null;
  }
  if (!isRiskLevel(riskLevel)) {
    return null;
  }

  return {
    title: title.trim(),
    description: description.trim(),
    riskLevel,
  };
};

const parseGoalDraft = (draft: unknown): DiagnosisUnlockGoalDraft | null => {
  if (!draft || typeof draft !== "object") {
    return null;
  }

  const { goalId, title, summary, trackerSpecs } = draft as Record<
    string,
    unknown
  >;

  if (typeof title !== "string" || !title.trim()) {
    return null;
  }
  if (typeof summary !== "string" || !summary.trim()) {
    return null;
  }
  if (!Array.isArray(trackerSpecs)) {
    return null;
  }

  const specs = trackerSpecs
    .map((spec) => {
      if (!spec || typeof spec !== "object") {
        return null;
      }

      const specTitle = (spec as Record<string, unknown>).title;
      if (typeof specTitle !== "string" || !specTitle.trim()) {
        return null;
      }

      const specDescription = (spec as Record<string, unknown>).description;
      return {
        title: specTitle.trim(),
        ...(typeof specDescription === "string" && specDescription.trim()
          ? { description: specDescription.trim() }
          : {}),
      };
    })
    .filter(
      (spec): spec is DiagnosisUnlockGoalDraft["trackerSpecs"][number] =>
        spec != null,
    );

  if (specs.length === 0) {
    return null;
  }

  return {
    ...(typeof goalId === "string" && goalId.trim()
      ? { goalId: goalId.trim() }
      : {}),
    title: title.trim(),
    summary: summary.trim(),
    trackerSpecs: specs,
  };
};

/** 将 `present_risk_and_goals` tool args 解析为弹窗 payload 与卡片数据；无效时返回 null */
export const parsePresentRiskAndGoalsArgs = (
  args: unknown,
): ParsedPresentRiskAndGoals | null => {
  if (!args || typeof args !== "object") {
    return null;
  }

  const raw = args as PresentRiskAndGoalsArgsLike;
  const title =
    (typeof raw.title === "string" && raw.title.trim()) ||
    (typeof raw.summary === "string" && raw.summary.trim()) ||
    "";
  const riskSummary =
    (typeof raw.summary === "string" && raw.summary.trim()) || "";

  const riskItems = Array.isArray(raw.riskItems)
    ? raw.riskItems
        .map(parseRiskItem)
        .filter((item): item is DiagnosisUnlockRiskItem => item != null)
    : [];

  const goalDraft = parseGoalDraft(raw.goalDraft);

  if (!riskSummary || riskItems.length === 0 || !goalDraft) {
    return null;
  }

  const domainId = resolveCardDomainId(raw);
  if (!domainId) {
    return null;
  }

  const payload: DiagnosisUnlockModalPayload = {
    riskSummary: title,
    riskItems,
    goalDraft,
  };

  const card: PlanCheckupCompletedCardData = {
    id: goalDraft.goalId ?? "",
    goalId: goalDraft.goalId ?? "",
    title: goalDraft.title ?? "",
    goalTitle: goalDraft.title ?? "",
    goalStatus: GoalStatusEnum.INACTIVE,
    kind: "completed",
    dimension: getDefaultDimensionForDomain(domainId),
    dimensionType: getDefaultDimensionTypeForDomain(domainId),
    domainId,
    summarySentence: riskSummary,
    status: "ready",
  };

  return { payload, card };
};

/** 解析 tool args；字段不完整时返回 null（不做 mock 回填） */
export const resolvePresentRiskAndGoalsPresentation = (
  args: unknown,
): ParsedPresentRiskAndGoals | null => parsePresentRiskAndGoalsArgs(args);
